// src/services/wizardServices.js
import _ from "lodash";
import { XRPIcon } from "../components/cryptoicons/xrp";
import { BTCIcon } from "../components/cryptoicons/btc";
import { ETHIcon } from "../components/cryptoicons/eth";
// import { ADAIcon } from "../components/svgs/cryptoicons/ada";
// import { DogeIcon } from "../components/svgs/cryptoicons/doge";
// import { SOLIcon } from "../components/svgs/cryptoicons/sol";

// Enhanced mock data with more realistic values and proper categorization
const mockCryptoData = [
  {
    id: "bitcoin",
    symbol: "BTC",
    name: "Bitcoin",
    price: 94852.5,
    marketCap: 1878981789859,
    change1h: 0.45,
    change24h: 0.45,
    volume24h: 28945123456,
    categories: ["all", "marketCap", "top10"],
    icon: <BTCIcon />,
  },
  {
    id: "ethereum",
    symbol: "ETH",
    name: "Ethereum",
    price: 3153.39,
    marketCap: 379981789859,
    change1h: 0.45,
    change24h: 0.45,
    volume24h: 15678912345,
    categories: ["all", "marketCap", "top10", "defi"],
    icon: <ETHIcon />,
  },
  {
    id: "xrp",
    symbol: "XRP",
    name: "XRP",
    price: 2.54,
    marketCap: 146981789859,
    change1h: 0.45,
    change24h: 0.45,
    volume24h: 5678912345,
    categories: ["all", "top10"],
    icon: <XRPIcon />,
  },
  {
    id: "solana",
    symbol: "SOL",
    name: "Solana",
    price: 125.54,
    marketCap: 54981789859,
    change1h: 1.2,
    change24h: 2.3,
    volume24h: 4567891234,
    categories: ["all", "sol", "defi"],
  },
  {
    id: "cardano",
    symbol: "ADA",
    name: "Cardano",
    price: 0.89,
    marketCap: 31567891234,
    change1h: -0.3,
    change24h: 1.8,
    volume24h: 2345678912,
    categories: ["all", "defi"],
  },
  {
    id: "dogecoin",
    symbol: "DOGE",
    name: "Dogecoin",
    price: 0.12,
    marketCap: 15678912345,
    change1h: 2.1,
    change24h: -1.5,
    volume24h: 1234567891,
    categories: ["all", "memes"],
  },
];

const categories = [
  { id: "all", label: "All Crypto", count: null },
  { id: "categories", label: "Categories", count: null },
  {
    id: "memes",
    label: "Memes",
    count: null
    // count: mockCryptoData.filter((c) => c.categories.includes("memes")).length,
  },
  {
    id: "sol",
    label: "SOL",
    count: null
    // count: mockCryptoData.filter((c) => c.categories.includes("sol")).length,
  },
  {
    id: "depin",
    label: "DePIN",
    count: null
    // count: mockCryptoData.filter((c) => c.categories.includes("depin")).length,
  },
  {
    id: "ai",
    label: "AI",
    count: null
    // count: mockCryptoData.filter((c) => c.categories.includes("ai")).length,
  },
];

// Cache management
let dataCache = {
  cryptos: null,
  categories: null,
  lastUpdated: null,
  updateInterval: 60000, // 1 minute
};

// Helper function to simulate price and market data updates
const updateMarketData = (data) => {
  return data.map((crypto) => {
    const volatility = 0.002; // 0.2% base volatility
    const priceChange = (Math.random() - 0.5) * 2 * volatility;
    const newPrice = crypto.price * (1 + priceChange);

    return {
      ...crypto,
      price: parseFloat(newPrice.toFixed(2)),
      change1h: parseFloat(
        (crypto.change1h + (Math.random() - 0.5) * 0.1).toFixed(2)
      ),
      change24h: parseFloat(
        (crypto.change24h + (Math.random() - 0.5) * 0.1).toFixed(2)
      ),
      marketCap: Math.floor(newPrice * (crypto.marketCap / crypto.price)),
    };
  });
};

// Get categories with caching
export const getCategories = async () => {
  if (
    dataCache.categories &&
    Date.now() - dataCache.lastUpdated < dataCache.updateInterval
  ) {
    return dataCache.categories;
  }

  await new Promise((resolve) => setTimeout(resolve, 50));
  dataCache.categories = categories;
  dataCache.lastUpdated = Date.now();
  return categories;
};

// Enhanced search with performance optimizations
export const searchCryptos = async (
  searchTerm = "",
  category = "all",
  limit = 50
) => {
  // Update cache if needed
  if (
    !dataCache.cryptos ||
    Date.now() - dataCache.lastUpdated >= dataCache.updateInterval
  ) {
    dataCache.cryptos = updateMarketData(mockCryptoData);
    dataCache.lastUpdated = Date.now();
  }

  let filteredData = dataCache.cryptos;

  // Apply filters
  if (category !== "all") {
    filteredData = filteredData.filter((crypto) =>
      crypto.categories.includes(category.toLowerCase())
    );
  }

  if (searchTerm) {
    const term = searchTerm.toLowerCase();
    filteredData = filteredData.filter(
      (crypto) =>
        crypto.name.toLowerCase().includes(term) ||
        crypto.symbol.toLowerCase().includes(term)
    );
  }

  // Sort by market cap by default
  filteredData = _.orderBy(filteredData, ["marketCap"], ["desc"]);

  // Apply pagination
  return filteredData.slice(0, limit);
};

// Get single crypto with caching
export const getCryptoById = async (id) => {
  if (!dataCache.cryptos) {
    await searchCryptos(); // Initialize cache
  }

  return dataCache.cryptos.find((crypto) => crypto.id === id);
};

// Helper function to format numbers
export const formatNumber = (number, decimals = 2) => {
  if (number >= 1e9) {
    return `$${(number / 1e9).toFixed(decimals)}B`;
  }
  if (number >= 1e6) {
    return `$${(number / 1e6).toFixed(decimals)}M`;
  }
  if (number >= 1e3) {
    return `$${(number / 1e3).toFixed(decimals)}K`;
  }
  return `$${number.toFixed(decimals)}`;
};

// Export utility functions
export const utils = {
  formatNumber,
  updateMarketData,
};

export const getMaxUSDC = async () => {
  // Mock service - replace with actual wallet integration
  return 23000; // Mock max USDC available
};
